import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useAtomValue } from 'jotai';
import { debounce } from 'lodash';
import { useRouter } from 'next/router';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { creditNudgeAtom } from '@/atoms/creditNudge';
import { hasActiveDialogueAtom } from '@/atoms/dialogQueue';
import { isDialogStageOpenAtom } from '@/components/DialogStage/atom';
import { PagePopup } from '@/components/PagePopup';
import { getNextInQueue } from '@/components/PagePopup/utils/getNextInQueue';
import { setAsDismissedInStorage } from '@/components/PagePopup/utils/setAsDismissedInStorage';
import { getReferredVisitorPopupId, logReferredVisitorPopupEvent } from '@/components/ReferredVisitorPopup/tracking';
import { useReferredVisitorPopup } from '@/components/ReferredVisitorPopup/useReferredVisitorPopup';
import { getExploreHref } from '@/components/ReferredVisitorPopup/copy';
import { ReferredVisitorPopupVariantC } from '@/components/ReferredVisitorPopup/VariantC';
import { getUniversalCookie } from '@/cookies';
import { useHasRole, useIsLoggedIn } from '@/hooks/auth';
import { useReferralAlerts } from '@/hooks/useReferralAlerts';
import { useValidateCoupon } from '@/hooks/billing/useValidateCoupon';
import { logEvent } from '@/pages/api/v1/utils';
import { getCouponPopupProps } from './popups/getCouponPopupProps';
import type { PagePopupProps } from '@/components/PagePopup/PagePopup';
import type { ReferredVisitorPopupEventType } from '@/components/ReferredVisitorPopup/tracking';
import { useOpenPartnerApplication } from '@/components/PartnerProgram/useOpenPartnerApplication';
import { useOverduePayment } from '@/hooks/billing/useOverduePayment';
import { getSessionId } from '@/pages/api/v1/utils';
import { usePartnerProgramPopup } from './hooks/usePartnerProgramPopup';
import { getFailedPaymentPopupProps } from './popups/getFailedPaymentPopupProps';
import { getInstagramCameraGiveawayProps } from './popups/getInstagramCameraGiveawayProps';
import { getInstagramMicMiniGiveaway2026Props } from './popups/getInstagramMicMiniGiveaway2026Props';
import { getLiminalAmbientDeepDiveProps } from './popups/getLiminalAmbientDeepDiveProps';
import { getInstagramMonthlyGiveaway2026Props } from './popups/getInstagramMonthlyGiveaway2026Props';
import { getReferralProgramProps } from './popups/getReferralProgramProps';
import { getAiSurvey2026Props } from './popups/getAiSurvey2026Props';
import { getTiktokGiveaway2026Props } from './popups/getTiktokGiveaway2026Props';
export interface PagePopupQueueProps {
  items: PagePopupProps[];
}
const PagePopupQueue = () => {
  const [items, setItems] = useState<PagePopupProps[]>([]);
  const hasActiveDialogue = useAtomValue(hasActiveDialogueAtom);
  const isDialogStageOpen = useAtomValue(isDialogStageOpenAtom);
  const creditNudge = useAtomValue(creditNudgeAtom);
  const router = useRouter();
  const {
    asPath
  } = router;
  const isLoggedIn = useIsLoggedIn();
  const isFree = useHasRole('Free');
  const {
    isUpgradeFlow
  } = useReferralAlerts();

  // DEV-6337: off by default — the referral popup was distracting users
  // during activation. Re-enable via the GrowthBook flag if needed.
  const showReferralPopupFlag = useFeatureIsOn('show-free-referral-popup');
  const {
    isValid,
    isValidating,
    alreadyApplied
  } = useValidateCoupon();
  const openPartnerApplication = useOpenPartnerApplication();
  const {
    isEligible: isPartnerPopupEligible,
    markShown: markPartnerPopupShown
  } = usePartnerProgramPopup();
  const {
    showPopup,
    overduePayment,
    setOverduePaymentState
  } = useOverduePayment();
  const logPopupClick = useCallback((type: string, popup_id: string, nextPath: string) => {
    logEvent({
      event_type: 'click',
      asPath,
      metadata: {
        type,
        popup_id,
        next_path: nextPath
      }
    });
  }, [asPath]);
  const {
    content: referredVisitorContent,
    partnerKey,
    showCreatorPopup,
    isResolving: isReferredVisitorResolving
  } = useReferredVisitorPopup();
  const referredVisitorPopupId = showCreatorPopup && partnerKey ? getReferredVisitorPopupId(partnerKey) : null;

  // PagePopup re-fires onShow on every navigation; count one per page load
  const loggedReferredImpressionsRef = useRef(new Set<string>());
  const hasClickedReferredCtaRef = useRef(false);
  const [closedBarePopupKey, setClosedBarePopupKey] = useState<string | null>(null);
  const trackReferredVisitorPopup = useCallback((type: ReferredVisitorPopupEventType, nextPath?: string) => {
    if (!(referredVisitorContent && partnerKey && referredVisitorPopupId)) {
      return;
    }
    if (type === 'impression') {
      if (loggedReferredImpressionsRef.current.has(referredVisitorPopupId)) {
        return;
      }
      loggedReferredImpressionsRef.current.add(referredVisitorPopupId);
    }
    void logReferredVisitorPopupEvent({
      type,
      popupId: referredVisitorPopupId,
      content: referredVisitorContent,
      partnerKey,
      asPath,
      nextPath: nextPath ?? asPath
    });
  }, [asPath, referredVisitorContent, partnerKey, referredVisitorPopupId]);
  const couponPopupProps: PagePopupProps | null = useMemo(() => {
    const coupon = getUniversalCookie('coupon');
    if (!coupon) {
      return null;
    }
    if (isValidating || !isValid || alreadyApplied) {
      return null;
    }
    const props = getCouponPopupProps({
      logPopupClick,
      asPath,
      coupon
    });
    if (!props) {
      return null;
    }
    return {
      ...props,
      excludeSlugs: [...(props.excludeSlugs || []), '/pricing'],
      children: props.children
    };
  }, [alreadyApplied, asPath, isValid, isValidating, logPopupClick]);
  const referredVisitorPopup: PagePopupProps | null = useMemo(() => {
    if (!(referredVisitorContent && referredVisitorPopupId)) {
      return null;
    }
    const markVariantCDismissed = () => {
      setAsDismissedInStorage({
        key: referredVisitorPopupId,
        storage: 'sessionStorage'
      });
    };
    return {
      storageKey: referredVisitorPopupId,
      storageType: 'sessionStorage',
      excludeSlugs: ['/pricing', '/my/account', '/music/latest-releases'],
      bareChildren: true,
      children: <ReferredVisitorPopupVariantC content={referredVisitorContent} exploreHref={getExploreHref(asPath)} onClose={() => {
        markVariantCDismissed();
        if (!hasClickedReferredCtaRef.current) {
          trackReferredVisitorPopup('dismiss');
        }
        setClosedBarePopupKey(referredVisitorPopupId);
      }} onCtaClick={href => {
        markVariantCDismissed();
        hasClickedReferredCtaRef.current = true;
        trackReferredVisitorPopup('cta', href);
      }} onShow={() => {
        trackReferredVisitorPopup('impression');
      }} />
    };
  }, [asPath, referredVisitorContent, referredVisitorPopupId, trackReferredVisitorPopup]);
  const ReferralProgram: PagePopupProps | null = useMemo(() => {
    const props = getReferralProgramProps({
      logPopupClick,
      asPath,
      onApply: openPartnerApplication
    });
    if (!props) {
      return null;
    }
    return {
      ...props,
      // The widget only ever shows once — persist the impression so future
      // sessions skip it even if the user never dismisses it (DEV-6570)
      onShow: () => {
        props.onShow?.();
        markPartnerPopupShown();
      }
    };
  }, [asPath, logPopupClick, openPartnerApplication, markPartnerPopupShown]);
  const InstagramCameraGiveawayProps: PagePopupProps | null = useMemo(() => {
    const props = getInstagramCameraGiveawayProps({
      logPopupClick,
      asPath
    });
    if (!props) {
      return null;
    }
    return props;
  }, [asPath, logPopupClick]);
  const TiktokGiveaway2026Props: PagePopupProps | null = useMemo(() => {
    const props = getTiktokGiveaway2026Props({
      logPopupClick,
      asPath
    });
    if (!props) {
      return null;
    }
    return props;
  }, [asPath, logPopupClick]);
  const InstagramMicMiniGiveaway2026Props: PagePopupProps | null = useMemo(() => {
    const props = getInstagramMicMiniGiveaway2026Props({
      logPopupClick,
      asPath
    });
    if (!props) {
      return null;
    }
    return props;
  }, [asPath, logPopupClick]);
  const InstagramMonthlyGiveaway2026Props: PagePopupProps | null = useMemo(() => {
    const props = getInstagramMonthlyGiveaway2026Props({
      logPopupClick,
      asPath
    });
    if (!props) {
      return null;
    }
    return props;
  }, [asPath, logPopupClick]);
  const AiSurvey2026Props: PagePopupProps | null = useMemo(() => {
    const props = getAiSurvey2026Props({
      logPopupClick,
      asPath
    });
    if (!props) {
      return null;
    }
    return props;
  }, [asPath, logPopupClick]);
  const LiminalAmbientDeepDiveProps: PagePopupProps = useMemo(() => getLiminalAmbientDeepDiveProps({
    logPopupClick,
    asPath
  }), [asPath, logPopupClick]);
  const FailedPaymentPopup: PagePopupProps | null = useMemo(() => {
    if (!showPopup) {
      return null;
    }
    return getFailedPaymentPopupProps({
      onClick: () => {
        const sessionId = getSessionId();
        setOverduePaymentState(prev => ({
          ...prev,
          showBanner: true,
          showPopup: false,
          sessions: [{
            id: sessionId
          }]
        }));
      },
      revalidateKey: undefined
    });
  }, [showPopup, setOverduePaymentState]);
  const getItems = useCallback(() => {
    const popupsToShow: (PagePopupProps | null)[] = [];
    if (FailedPaymentPopup) {
      popupsToShow.push(FailedPaymentPopup);
    }

    // Hold everything else back until we know whether the partner's or coupon
    // pop-up takes the slot, or a giveaway can get in first (delays only apply on /)
    if (isReferredVisitorResolving || isValidating) {
      return FailedPaymentPopup ? [FailedPaymentPopup] : [];
    }
    if (referredVisitorPopup) {
      popupsToShow.push(referredVisitorPopup);
    } else if (couponPopupProps) {
      popupsToShow.push(couponPopupProps);
    }
    if (AiSurvey2026Props) {
      popupsToShow.push(AiSurvey2026Props);
    }
    popupsToShow.push(LiminalAmbientDeepDiveProps);
    if (InstagramCameraGiveawayProps) {
      popupsToShow.push(InstagramCameraGiveawayProps);
    }
    if (TiktokGiveaway2026Props) {
      popupsToShow.push(TiktokGiveaway2026Props);
    }
    if (InstagramMicMiniGiveaway2026Props) {
      popupsToShow.push(InstagramMicMiniGiveaway2026Props);
    }
    if (InstagramMonthlyGiveaway2026Props) {
      popupsToShow.push(InstagramMonthlyGiveaway2026Props);
    }
    const showPartnerProgramPopup = isLoggedIn && !isUpgradeFlow && showReferralPopupFlag && isPartnerPopupEligible;
    if (showPartnerProgramPopup) {
      popupsToShow.push(ReferralProgram);
    }
    return popupsToShow.filter((p): p is PagePopupProps => p !== null);
  }, [ReferralProgram, couponPopupProps, referredVisitorPopup, isReferredVisitorResolving, isValidating, isLoggedIn, isUpgradeFlow, showReferralPopupFlag, isPartnerPopupEligible, FailedPaymentPopup, InstagramCameraGiveawayProps, TiktokGiveaway2026Props, InstagramMicMiniGiveaway2026Props, InstagramMonthlyGiveaway2026Props, AiSurvey2026Props, LiminalAmbientDeepDiveProps]);
  const debouncedGetItemsRef = useRef(debounce((getItemsFn: () => PagePopupProps[]) => {
    const popupItems = getItemsFn();
    setItems(popupItems);
    // Anything below 1500 may cause popups appear briefly while the queue is being updated
  }, 1500));
  useEffect(() => {
    debouncedGetItemsRef.current(getItems);
    return () => {
      debouncedGetItemsRef.current.cancel();
    };
  }, [getItems]);
  const nextToShowIndex = useMemo(() => getNextInQueue({
    items,
    pathname: asPath
  }), [items, asPath]);
  const currentItem = useMemo(() => {
    if (!items || items.length === 0) {
      return null;
    }
    if (nextToShowIndex < 0 || nextToShowIndex >= items.length) {
      return null;
    }
    return items[nextToShowIndex];
  }, [items, nextToShowIndex]);

  // Suppress every page-level popup on /subscribe — the checkout needs to
  // stay distraction-free. Only toast notifications (for payment / auth
  // errors) should reach the user during checkout.
  const isOnCheckout = asPath.startsWith('/subscribe');
  const isCreditNudgePending = creditNudge === 'pending' && isFree;

  // Defer while a stage flow (download journey, auth…) is open — a popup
  // must not appear behind or on top of it (DEV-6707)
  if (hasActiveDialogue || isDialogStageOpen || isCreditNudgePending || isOnCheckout || !currentItem) {
    return null;
  }
  if (currentItem.bareChildren) {
    if (currentItem.storageKey === closedBarePopupKey) {
      return null;
    }
    return <Fragment key={currentItem.storageKey}>{currentItem.children}</Fragment>;
  }
  const popupKey = currentItem?.revalidateKey ? `${currentItem.storageKey}-${currentItem.revalidateKey}` : undefined;
  return <PagePopup key={popupKey} {...currentItem} data-sentry-element="PagePopup" data-sentry-component="PagePopupQueue" data-sentry-source-file="index.tsx">
      {currentItem.children}
    </PagePopup>;
};
export default PagePopupQueue;